import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertTriangle, CheckCircle2, Download, FileText, History, Loader2, ShieldCheck, Trash2, Upload, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import {
  API_URL, METHODES_VERIFICATION, STATUT_DOCUMENT, STATUT_VERIFICATION,
  fetchAndDownload, fileToBase64, formatDateFr, formatDateTimeFr, joursRestantsLabel
} from './constants';

const ACCEPTED = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const MAX_BYTES = 10 * 1024 * 1024;

const addMonths = (iso, months) => {
  if (!iso || !months) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + Number(months));
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

// ------------------------------------------------------------------ Dialogue de dépôt

const UploadDialog = ({ open, onOpenChange, sousTraitantId, types, defaultType, onUploaded }) => {
  const [form, setForm] = useState({ type_code: '', date_emission: todayIso(), date_expiration: '', numero_reference: '', commentaire: '' });
  const [file, setFile] = useState(null);
  const [expirationTouched, setExpirationTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedType = types.find((t) => t.code === form.type_code);

  useEffect(() => {
    if (open) {
      const type_code = defaultType || types[0]?.code || '';
      const t = types.find((x) => x.code === type_code);
      const date_emission = todayIso();
      setForm({
        type_code, date_emission, numero_reference: '', commentaire: '',
        date_expiration: t?.validite_mois ? addMonths(date_emission, t.validite_mois) : ''
      });
      setFile(null);
      setExpirationTouched(false);
    }
  }, [open, defaultType, types]);

  const updateType = (code) => {
    const t = types.find((x) => x.code === code);
    setForm((f) => ({
      ...f, type_code: code,
      date_expiration: !expirationTouched && t?.validite_mois ? addMonths(f.date_emission, t.validite_mois) : (expirationTouched ? f.date_expiration : '')
    }));
  };

  const updateEmission = (value) => {
    setForm((f) => ({
      ...f, date_emission: value,
      date_expiration: !expirationTouched && selectedType?.validite_mois ? addMonths(value, selectedType.validite_mois) : f.date_expiration
    }));
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) { toast.error('Fichier trop volumineux (10 Mo maximum)'); e.target.value = ''; return; }
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(f.type)) { toast.error('Formats acceptés : PDF, JPG, PNG'); e.target.value = ''; return; }
    setFile(f);
  };

  const submit = async () => {
    if (!form.type_code) { toast.error('Choisissez un type de document'); return; }
    if (!file) { toast.error('Sélectionnez un fichier'); return; }
    setSaving(true);
    try {
      const data = await fileToBase64(file);
      const res = await axios.post(`${API_URL}/sous-traitants/${sousTraitantId}/documents`, {
        type_code: form.type_code,
        fichier_nom: file.name,
        content_type: file.type,
        data,
        date_emission: form.date_emission || null,
        date_expiration: form.date_expiration || null,
        numero_reference: form.numero_reference || null,
        commentaire: form.commentaire || null
      });
      toast.success('Document déposé (à vérifier)');
      onUploaded?.(res.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Dépôt impossible'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Déposer un document</DialogTitle>
          <DialogDescription>Un nouveau dépôt remplace la version courante du même type, qui reste archivée dans l'historique.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label>Type de document</Label>
            <Select value={form.type_code} onValueChange={updateType}>
              <SelectTrigger><SelectValue placeholder="Choisir un type" /></SelectTrigger>
              <SelectContent>
                {types.filter((t) => t.actif !== false).map((t) => (
                  <SelectItem key={t.code} value={t.code}>{t.label}{t.obligatoire ? ' *' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedType?.description && <p className="text-xs text-slate-500">{selectedType.description}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="doc-emission">Date d'émission</Label>
              <Input id="doc-emission" type="date" value={form.date_emission} onChange={(e) => updateEmission(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="doc-expiration">Date d'expiration</Label>
              <Input id="doc-expiration" type="date" value={form.date_expiration}
                onChange={(e) => { setExpirationTouched(true); setForm((f) => ({ ...f, date_expiration: e.target.value })); }} />
              <p className="text-xs text-slate-500">
                {selectedType?.validite_mois ? `Calculée : émission + ${selectedType.validite_mois} mois (ajustable)` : 'Saisir l\'échéance si le document en a une'}
              </p>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="doc-ref">Numéro / code de sécurité</Label>
            <Input id="doc-ref" value={form.numero_reference} onChange={(e) => setForm((f) => ({ ...f, numero_reference: e.target.value }))}
              placeholder={form.type_code === 'attestation_vigilance' ? 'Code de sécurité URSSAF' : 'Référence du document'} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="doc-file">Fichier (PDF, JPG, PNG — 10 Mo max)</Label>
            <Input id="doc-file" type="file" accept={ACCEPTED} onChange={handleFile} />
            {file && <p className="text-xs text-slate-500">{file.name} — {(file.size / 1024).toFixed(0)} Ko</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="doc-comment">Commentaire</Label>
            <Textarea id="doc-comment" rows={2} value={form.commentaire} onChange={(e) => setForm((f) => ({ ...f, commentaire: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0F5156] hover:bg-[#0F5156]/90">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Déposer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ------------------------------------------------------------------ Dialogue de vérification

const VerifyDialog = ({ open, onOpenChange, sousTraitantId, doc, onVerified }) => {
  const [form, setForm] = useState({ statut_verification: 'verifie', methode_verification: 'visuel', commentaire: '', numero_reference: '', date_expiration: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && doc) {
      setForm({
        statut_verification: 'verifie',
        methode_verification: doc.type_code === 'attestation_vigilance' ? 'code_urssaf' : 'visuel',
        commentaire: '',
        numero_reference: doc.numero_reference || '',
        date_expiration: doc.date_expiration || ''
      });
    }
  }, [open, doc]);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/sous-traitants/${sousTraitantId}/documents/${doc.id}/verifier`, {
        statut_verification: form.statut_verification,
        methode_verification: form.methode_verification,
        commentaire: form.commentaire || null,
        numero_reference: form.numero_reference,
        date_expiration: form.date_expiration
      });
      toast.success(form.statut_verification === 'rejete' ? 'Document rejeté' : 'Document vérifié');
      onVerified?.(res.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Vérification impossible'));
    } finally {
      setSaving(false);
    }
  };

  if (!doc) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Vérifier le document</DialogTitle>
          <DialogDescription>{doc.type_label} — {doc.fichier_nom}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Décision</Label>
              <Select value={form.statut_verification} onValueChange={(v) => setForm((f) => ({ ...f, statut_verification: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="verifie">Vérifié — conforme</SelectItem>
                  <SelectItem value="rejete">Rejeté — non recevable</SelectItem>
                  <SelectItem value="a_verifier">Remettre à vérifier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Méthode</Label>
              <Select value={form.methode_verification} onValueChange={(v) => setForm((f) => ({ ...f, methode_verification: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(METHODES_VERIFICATION).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ver-ref">Numéro / code de sécurité</Label>
              <Input id="ver-ref" value={form.numero_reference} onChange={(e) => setForm((f) => ({ ...f, numero_reference: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ver-exp">Date d'expiration</Label>
              <Input id="ver-exp" type="date" value={form.date_expiration} onChange={(e) => setForm((f) => ({ ...f, date_expiration: e.target.value }))} />
            </div>
          </div>
          {form.methode_verification === 'code_urssaf' && (
            <p className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-800 rounded p-2">
              Vérifiez l'authenticité sur <span className="font-medium">urssaf.fr › Vérifier une attestation</span> avec le code de sécurité et le SIRET du sous-traitant.
            </p>
          )}
          <div className="space-y-1">
            <Label htmlFor="ver-comment">Commentaire {form.statut_verification === 'rejete' && '(motif du rejet)'}</Label>
            <Textarea id="ver-comment" rows={2} value={form.commentaire} onChange={(e) => setForm((f) => ({ ...f, commentaire: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className={form.statut_verification === 'rejete' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#0F5156] hover:bg-[#0F5156]/90'}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ------------------------------------------------------------------ Ligne document

const DocumentRow = ({ doc, sousTraitantId, canEdit, canControl, onVerify, onDeleted, compact }) => {
  const [busy, setBusy] = useState(false);
  const statut = STATUT_DOCUMENT[doc.statut] || STATUT_DOCUMENT.valide;
  const verif = STATUT_VERIFICATION[doc.statut_verification] || STATUT_VERIFICATION.a_verifier;

  const download = async (openInTab) => {
    setBusy(true);
    try {
      await fetchAndDownload(`${API_URL}/sous-traitants/${sousTraitantId}/documents/${doc.id}/download`, { openInTab });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Téléchargement impossible'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer définitivement ce document rejeté ?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/sous-traitants/${sousTraitantId}/documents/${doc.id}`);
      toast.success('Document supprimé');
      onDeleted?.(doc);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Suppression impossible'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 ${compact ? 'py-2 pl-6 text-sm opacity-80' : 'py-3'}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <FileText className="w-4 h-4 text-slate-400 shrink-0" />
          <button type="button" onClick={() => download(true)} className="font-medium truncate hover:underline text-left" title="Ouvrir">
            {doc.fichier_nom}
          </button>
          {!doc.archive && <Badge className={statut.color}>{statut.label}</Badge>}
          <Badge className={verif.color}>{verif.label}</Badge>
          {doc.archive && <Badge variant="outline">Version archivée</Badge>}
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Émis le {formatDateFr(doc.date_emission)}
          {doc.date_expiration ? ` — expire le ${formatDateFr(doc.date_expiration)}${!doc.archive && doc.jours_restants !== null && doc.jours_restants !== undefined ? ` (${joursRestantsLabel(doc.jours_restants)})` : ''}` : ' — sans expiration'}
          {doc.numero_reference ? ` — réf. ${doc.numero_reference}` : ''}
          {' — déposé le '}{formatDateTimeFr(doc.uploaded_at)}
        </p>
        {doc.verifie_le && (
          <p className="text-xs text-slate-500">
            {doc.statut_verification === 'rejete' ? 'Rejeté' : 'Vérifié'} par {doc.verifie_par || doc.verifie_par_email} le {formatDateTimeFr(doc.verifie_le)}
            {doc.methode_verification ? ` (${METHODES_VERIFICATION[doc.methode_verification] || doc.methode_verification})` : ''}
          </p>
        )}
        {doc.commentaire && <p className="text-xs italic text-slate-500">« {doc.commentaire} »</p>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button variant="ghost" size="sm" onClick={() => download(false)} disabled={busy} title="Télécharger"><Download className="w-4 h-4" /></Button>
        {canControl && !doc.archive && (
          <Button variant="ghost" size="sm" onClick={() => onVerify(doc)} title="Vérifier">
            {doc.statut_verification === 'verifie' ? <CheckCircle2 className="w-4 h-4 text-blue-600" /> : <ShieldCheck className="w-4 h-4" />}
          </Button>
        )}
        {canEdit && doc.statut_verification === 'rejete' && (
          <Button variant="ghost" size="sm" onClick={remove} disabled={busy} title="Supprimer (rejeté)"><Trash2 className="w-4 h-4 text-red-500" /></Button>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ Onglet

/**
 * Documents de conformité d'un sous-traitant, groupés par type avec statut, dépôt,
 * vérification (contrôleurs), téléchargement et historique des versions.
 */
const SousTraitantDocumentsTab = ({ sousTraitant, canEdit, canControl, onChanged }) => {
  const [data, setData] = useState({ documents: [], types: [], documents_requis: [] });
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadType, setUploadType] = useState(null);
  const [verifyDoc, setVerifyDoc] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/sous-traitants/${sousTraitant.id}/documents`);
      setData(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Chargement des documents impossible'));
    } finally {
      setLoading(false);
    }
  }, [sousTraitant.id]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); };

  const groups = useMemo(() => {
    const requis = new Set(data.documents_requis || []);
    const byType = {};
    (data.documents || []).forEach((d) => { (byType[d.type_code] = byType[d.type_code] || []).push(d); });
    const activeTypes = (data.types || []).filter((t) => t.actif !== false || byType[t.code]);
    const known = new Set(activeTypes.map((t) => t.code));
    Object.keys(byType).forEach((code) => { if (!known.has(code)) activeTypes.push({ code, label: byType[code][0]?.type_label || code }); });
    return activeTypes.map((t) => {
      const docs = byType[t.code] || [];
      const current = docs.find((d) => !d.archive && d.statut_verification !== 'rejete') || null;
      const others = docs.filter((d) => d !== current);
      return { type: t, requis: requis.has(t.code), current, others };
    }).sort((a, b) => Number(b.requis) - Number(a.requis));
  }, [data]);

  if (loading) {
    return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-[#0F5156]" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">
          Les documents marqués <span className="font-semibold">obligatoire</span> conditionnent la conformité. Périodicité légale : 6 mois.
        </p>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <History className="w-4 h-4 text-slate-400" />
            <span>Versions archivées</span>
            <Switch checked={showHistory} onCheckedChange={setShowHistory} />
          </label>
          {canEdit && (
            <Button size="sm" onClick={() => { setUploadType(null); setUploadOpen(true); }} className="bg-[#0F5156] hover:bg-[#0F5156]/90">
              <Upload className="w-4 h-4 mr-2" />Déposer un document
            </Button>
          )}
        </div>
      </div>

      <div className="divide-y rounded-lg border dark:border-slate-700">
        {groups.map(({ type, requis, current, others }) => {
          const missing = requis && !current;
          return (
            <div key={type.code} className="px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {missing ? <AlertTriangle className="w-4 h-4 text-red-500" /> : current ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-slate-300" />}
                  <span className="font-semibold">{type.label}</span>
                  {requis && <Badge variant="outline" className="text-[10px]">obligatoire</Badge>}
                  {type.validite_mois ? <span className="text-xs text-slate-400">validité {type.validite_mois} mois</span> : null}
                  {missing && <Badge className={STATUT_DOCUMENT.manquant.color}>Manquant</Badge>}
                </div>
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={() => { setUploadType(type.code); setUploadOpen(true); }}>
                    <Upload className="w-3.5 h-3.5 mr-1.5" />{current ? 'Nouvelle version' : 'Déposer'}
                  </Button>
                )}
              </div>
              {type.description && !current && <p className="text-xs text-slate-500 mt-1 ml-6">{type.description}</p>}
              {current && (
                <DocumentRow doc={current} sousTraitantId={sousTraitant.id} canEdit={canEdit} canControl={canControl}
                  onVerify={setVerifyDoc} onDeleted={refresh} />
              )}
              {others.filter((d) => showHistory || d.statut_verification === 'rejete' && !d.archive).map((d) => (
                <DocumentRow key={d.id} doc={d} sousTraitantId={sousTraitant.id} canEdit={canEdit} canControl={canControl}
                  onVerify={setVerifyDoc} onDeleted={refresh} compact />
              ))}
            </div>
          );
        })}
        {groups.length === 0 && <p className="p-6 text-center text-sm text-slate-500">Aucun type de document configuré (Administration › Sous-traitants).</p>}
      </div>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} sousTraitantId={sousTraitant.id}
        types={data.types || []} defaultType={uploadType} onUploaded={refresh} />
      <VerifyDialog open={!!verifyDoc} onOpenChange={(o) => { if (!o) setVerifyDoc(null); }} sousTraitantId={sousTraitant.id}
        doc={verifyDoc} onVerified={refresh} />
    </div>
  );
};

export default SousTraitantDocumentsTab;
